import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  Plus, Trash2, Pencil, AlertTriangle, TrendingUp, TrendingDown,
  Wallet, Target, Shield, ShieldAlert, ShieldCheck, Loader2,
  DollarSign, Calendar, BarChart3, ArrowRight, Settings, Calculator, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PilotageFinancierPage() {
  const { token } = useAuth();
  const api = axios.create({ baseURL: `${API}/api`, headers: { Authorization: `Bearer ${token}` } });

  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [charges, setCharges] = useState([]);
  const [dettes, setDettes] = useState([]);
  const [indicateurs, setIndicateurs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selMois, setSelMois] = useState(new Date().getMonth() + 1);
  const [selAnnee, setSelAnnee] = useState(new Date().getFullYear());

  // Dialogs
  const [showChargeDialog, setShowChargeDialog] = useState(false);
  const [showDetteDialog, setShowDetteDialog] = useState(false);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Forms
  const [chargeForm, setChargeForm] = useState({ categorie: '', description: '', montant: '', frequence: 'mensuel', type: 'fixe' });
  const [detteForm, setDetteForm] = useState({ type: 'emprunt', libelle: '', montant_initial: '', montant_restant: '', taux: '', mensualite: '', date_debut: '', date_echeance: '', duree_mois: '', notes: '' });
  const [catForm, setCatForm] = useState({ name: '', type: 'fixe' });

  const fetchAll = useCallback(async () => {
    try {
      const [cfgR, catR, chgR, detR, indR] = await Promise.all([
        api.get('/pilotage/config'),
        api.get('/pilotage/categories'),
        api.get('/pilotage/charges'),
        api.get('/pilotage/dettes'),
        api.get('/pilotage/indicateurs', { params: { mois: selMois, annee: selAnnee } }),
      ]);
      setConfig(cfgR.data);
      setCategories(catR.data);
      setCharges(chgR.data);
      setDettes(detR.data);
      setIndicateurs(indR.data);
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  }, [selMois, selAnnee]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Config save
  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await api.put('/pilotage/config', config);
      setConfig(res.data);
      toast.success("Configuration sauvegardée");
      // Refresh indicators
      const indR = await api.get('/pilotage/indicateurs', { params: { mois: selMois, annee: selAnnee } });
      setIndicateurs(indR.data);
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  // Charges CRUD
  const openChargeDialog = (charge = null) => {
    if (charge) {
      setChargeForm(charge);
      setEditItem(charge);
    } else {
      setChargeForm({ categorie: categories[0]?.name || '', description: '', montant: '', frequence: 'mensuel', type: 'fixe' });
      setEditItem(null);
    }
    setShowChargeDialog(true);
  };

  const saveCharge = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/pilotage/charges/${editItem.id}`, chargeForm);
        toast.success("Charge mise à jour");
      } else {
        await api.post('/pilotage/charges', chargeForm);
        toast.success("Charge ajoutée");
      }
      setShowChargeDialog(false);
      fetchAll();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  // Inline update a single field on a charge (montant, description, frequence, categorie, type)
  const updateChargeField = async (chargeId, field, value) => {
    try {
      await api.put(`/pilotage/charges/${chargeId}`, { [field]: value });
      // Optimistic local update, then refresh indicators
      setCharges(prev => prev.map(c => c.id === chargeId ? { ...c, [field]: field === 'montant' ? parseFloat(value) || 0 : value } : c));
      const indR = await api.get('/pilotage/indicateurs', { params: { mois: selMois, annee: selAnnee } });
      setIndicateurs(indR.data);
    } catch {
      toast.error("Erreur mise à jour");
      fetchAll();
    }
  };

  // Inline update the "Coût alimentaire manuel" override
  const updateCoutAlimentaireManuel = async (value) => {
    try {
      const newConfig = { ...(config || {}), cout_alimentaire_manuel: parseFloat(value) || 0 };
      const res = await api.put('/pilotage/config', newConfig);
      setConfig(res.data);
      const indR = await api.get('/pilotage/indicateurs', { params: { mois: selMois, annee: selAnnee } });
      setIndicateurs(indR.data);
      toast.success(parseFloat(value) > 0 ? "Coût alimentaire override enregistré" : "Retour au calcul automatique");
    } catch {
      toast.error("Erreur");
    }
  };

  // Calcul auto de la marge brute
  const [showMargeCalc, setShowMargeCalc] = useState(false);
  const [margeCalcData, setMargeCalcData] = useState(null);
  const [margeCalcLoading, setMargeCalcLoading] = useState(false);

  const openMargeCalculator = async () => {
    setShowMargeCalc(true);
    setMargeCalcLoading(true);
    try {
      const res = await api.get('/pilotage/calcul-marge-brute');
      setMargeCalcData(res.data);
    } catch {
      toast.error("Erreur calcul marge");
      setShowMargeCalc(false);
    } finally {
      setMargeCalcLoading(false);
    }
  };

  const applyMarge = async (value) => {
    try {
      const newConfig = { ...(config || {}), marge_brute_pct: parseFloat(value) };
      const res = await api.put('/pilotage/config', newConfig);
      setConfig(res.data);
      const indR = await api.get('/pilotage/indicateurs', { params: { mois: selMois, annee: selAnnee } });
      setIndicateurs(indR.data);
      toast.success(`Marge brute mise à jour : ${value}%`);
      setShowMargeCalc(false);
    } catch {
      toast.error("Erreur");
    }
  };

  const deleteCharge = async (id) => {
    try {
      await api.delete(`/pilotage/charges/${id}`);
      toast.success("Charge supprimée");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  // Dettes CRUD
  const openDetteDialog = (dette = null) => {
    if (dette) {
      setDetteForm(dette);
      setEditItem(dette);
    } else {
      setDetteForm({ type: 'emprunt', libelle: '', montant_initial: '', montant_restant: '', taux: '', mensualite: '', date_debut: '', date_echeance: '', duree_mois: '', notes: '' });
      setEditItem(null);
    }
    setShowDetteDialog(true);
  };

  const saveDette = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/pilotage/dettes/${editItem.id}`, detteForm);
        toast.success("Dette mise à jour");
      } else {
        await api.post('/pilotage/dettes', detteForm);
        toast.success("Dette ajoutée");
      }
      setShowDetteDialog(false);
      fetchAll();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const deleteDette = async (id) => {
    try {
      await api.delete(`/pilotage/dettes/${id}`);
      toast.success("Dette supprimée");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  // Categories CRUD
  const saveCat = async () => {
    try {
      if (editItem) {
        await api.put(`/pilotage/categories/${editItem.id}`, catForm);
      } else {
        await api.post('/pilotage/categories', catForm);
      }
      setShowCatDialog(false);
      setEditItem(null);
      fetchAll();
      toast.success("Catégorie sauvegardée");
    } catch { toast.error("Erreur"); }
  };

  const deleteCat = async (id) => {
    try {
      await api.delete(`/pilotage/categories/${id}`);
      fetchAll();
      toast.success("Catégorie supprimée");
    } catch { toast.error("Erreur"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const fmtK = (n) => { const v = n || 0; return v >= 1000 ? `${(v/1000).toFixed(1)}k EUR` : fmt(v); };
  const ind = indicateurs || {};
  const riskColor = { faible: 'text-green-600', modéré: 'text-amber-600', élevé: 'text-orange-600', critique: 'text-red-600' };
  const riskBg = { faible: 'bg-green-50 border-green-200', modéré: 'bg-amber-50 border-amber-200', élevé: 'bg-orange-50 border-orange-200', critique: 'bg-red-50 border-red-200' };
  const RiskIcon = ind.niveau_risque === 'faible' ? ShieldCheck : ind.niveau_risque === 'critique' ? ShieldAlert : Shield;

  const conseils = ind.conseils || [];
  const conseilIconMap = {
    'alert': AlertTriangle, 'check': ShieldCheck, 'settings': Settings,
    'trending-up': TrendingUp, 'trending-down': TrendingDown, 'lightbulb': Target,
  };
  const conseilColorMap = {
    'alerte': 'border-red-300 bg-red-50/80', 'critique': 'border-red-400 bg-red-50',
    'attention': 'border-amber-300 bg-amber-50/80', 'positif': 'border-green-300 bg-green-50/80',
    'conseil': 'border-blue-300 bg-blue-50/80', 'info': 'border-slate-300 bg-slate-50/80',
    'setup': 'border-violet-300 bg-violet-50/80',
  };
  const conseilTextColor = {
    'alerte': 'text-red-800', 'critique': 'text-red-800', 'attention': 'text-amber-800',
    'positif': 'text-green-800', 'conseil': 'text-blue-800', 'info': 'text-slate-700', 'setup': 'text-violet-800',
  };

  // Compute simple overview numbers
  const solde = ind.solde_tresorerie || 0;
  const totalSorties = ind.total_mensuel || 0;
  const resteFinMois = solde + (ind.ca_objectif || 0) - totalSorties;
  const breakEvenJour = ind.break_even_jour || 0;

  return (
    <div className="space-y-6" data-testid="pilotage-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Pilotage Financier</h1>
            <p className="text-muted-foreground text-sm">Votre situation financiere en un coup d'oeil</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selMois.toString()} onValueChange={v => setSelMois(parseInt(v))}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="w-4 h-4 mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'].map((m, i) => (
                  <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selAnnee.toString()} onValueChange={v => setSelAnnee(parseInt(v))}>
              <SelectTrigger className="w-[90px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024,2025,2026,2027].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs defaultValue="synthese">
          <TabsList>
            <TabsTrigger value="synthese" data-testid="tab-synthese"><BarChart3 className="w-4 h-4 mr-1" /> Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="indicateurs" data-testid="tab-indicateurs"><Target className="w-4 h-4 mr-1" /> Details</TabsTrigger>
            <TabsTrigger value="charges" data-testid="tab-charges"><Wallet className="w-4 h-4 mr-1" /> Charges</TabsTrigger>
            <TabsTrigger value="dettes" data-testid="tab-dettes"><DollarSign className="w-4 h-4 mr-1" /> Dettes</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config"><Settings className="w-4 h-4 mr-1" /> Config</TabsTrigger>
          </TabsList>

          {/* =============== VUE D'ENSEMBLE TAB =============== */}
          <TabsContent value="synthese" className="space-y-6" data-testid="overview-tab">

            {/* 4 big simple cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Card 1: Tresorerie */}
              <Card className={`border-2 ${solde > 0 ? 'border-green-200' : 'border-red-200'}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">En banque</span>
                    <Wallet className={`w-5 h-5 ${solde > 0 ? 'text-green-500' : 'text-red-500'}`} />
                  </div>
                  <p className={`text-3xl font-bold tabular-nums ${solde > 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="overview-solde">
                    {fmt(solde)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {ind.mois_tresorerie > 0 ? `${ind.mois_tresorerie} mois de reserve` : 'Solde configure'}
                  </p>
                </CardContent>
              </Card>

              {/* Card 2: A payer ce mois */}
              <Card className="border-2 border-orange-200">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">A payer ce mois</span>
                    <TrendingDown className="w-5 h-5 text-orange-500" />
                  </div>
                  <p className="text-3xl font-bold tabular-nums text-orange-700" data-testid="overview-sorties">
                    {fmt(totalSorties)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Fixes {fmt(ind.charges_fixes_mensuelles)} + Var {fmt(ind.charges_variables_mensuelles)} + Dettes {fmt(ind.total_mensualites_dettes)}
                  </p>
                </CardContent>
              </Card>

              {/* Card 3: Il reste en fin de mois */}
              <Card className={`border-2 ${resteFinMois >= 0 ? 'border-emerald-200' : 'border-red-200'}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">Estimation fin de mois</span>
                    {resteFinMois >= 0
                      ? <TrendingUp className="w-5 h-5 text-emerald-500" />
                      : <AlertTriangle className="w-5 h-5 text-red-500" />}
                  </div>
                  <p className={`text-3xl font-bold tabular-nums ${resteFinMois >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="overview-reste">
                    {fmt(resteFinMois)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Banque + CA objectif - Charges
                  </p>
                </CardContent>
              </Card>

              {/* Card 4: Break-even */}
              <Card className="border-2 border-blue-200">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">Break-even / jour</span>
                    <Target className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold tabular-nums text-blue-700" data-testid="overview-breakeven">
                    {fmt(breakEvenJour)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Soit {fmt(ind.break_even_mensuel)} / mois
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Risk Level */}
            <Card className={`border-2 ${riskBg[ind.niveau_risque] || ''}`} data-testid="overview-risk">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <RiskIcon className={`w-10 h-10 ${riskColor[ind.niveau_risque]}`} />
                  <div className="flex-1">
                    <p className={`text-xl font-bold capitalize ${riskColor[ind.niveau_risque]}`}>
                      Risque {ind.niveau_risque || '-'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {ind.niveau_risque === 'faible' && 'Vos indicateurs sont sains. Continuez comme ca !'}
                      {ind.niveau_risque === 'modéré' && 'Attention, certains indicateurs meritent votre vigilance.'}
                      {ind.niveau_risque === 'élevé' && 'Situation tendue, des actions correctives sont recommandees.'}
                      {ind.niveau_risque === 'critique' && 'Situation critique ! Agissez rapidement pour eviter les difficultes.'}
                    </p>
                  </div>
                  {ind.ca_objectif > 0 && ind.break_even_mensuel > 0 && (
                    <div className="text-right hidden sm:block">
                      <div className={`text-2xl font-bold tabular-nums ${ind.ca_vs_breakeven >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {ind.ca_vs_breakeven >= 0 ? '+' : ''}{fmt(ind.ca_vs_breakeven)}
                      </div>
                      <p className="text-xs text-muted-foreground">vs break-even</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Prevision 6 mois - compact */}
            {ind.prevision_6_mois && ind.prevision_6_mois.length > 0 && (
              <Card data-testid="overview-prevision">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Prevision 6 mois
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {ind.prevision_6_mois.map((p, i) => (
                      <div key={i} className={`p-3 rounded-lg text-center border ${p.alerte ? 'border-red-300 bg-red-50' : 'border-muted bg-muted/30'}`}
                        data-testid={`overview-prev-${i}`}>
                        <p className="text-xs font-medium text-muted-foreground">{p.mois}</p>
                        <p className={`text-lg font-bold tabular-nums mt-1 ${p.solde < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {fmtK(p.solde)}
                        </p>
                        <div className="flex justify-center gap-2 mt-1">
                          <span className="text-[10px] text-green-600">+{fmtK(p.entrees)}</span>
                          <span className="text-[10px] text-red-500">-{fmtK(p.sorties)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Conseils */}
            {conseils.length > 0 && (
              <Card data-testid="overview-conseils">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4" /> Conseils & Recommandations
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {conseils.map((c, i) => {
                    const Icon = conseilIconMap[c.icone] || Target;
                    return (
                      <div key={i}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${conseilColorMap[c.type] || 'border-muted bg-muted/30'}`}
                        data-testid={`conseil-${i}`}>
                        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${conseilTextColor[c.type] || ''}`} />
                        <div>
                          <p className={`text-sm font-semibold ${conseilTextColor[c.type] || ''}`}>{c.titre}</p>
                          <p className="text-sm text-muted-foreground mt-0.5">{c.message}</p>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* =============== INDICATEURS TAB =============== */}
          <TabsContent value="indicateurs" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard title="Break-even / mois" value={fmt(ind.break_even_mensuel)} icon={Target}
                sub={`${fmt(ind.break_even_jour)} / jour`} color="text-blue-600" testid="kpi-breakeven" />
              <KpiCard title="Charges fixes / mois" value={fmt(ind.charges_fixes_mensuelles)} icon={TrendingDown}
                sub={`+ ${fmt(ind.charges_variables_mensuelles)} variables`} color="text-red-600" testid="kpi-charges" />
              <KpiCardEditable
                title="Coût alimentaire estimé"
                value={ind.variable_alimentaire}
                manualValue={ind.cout_alimentaire_manuel}
                isOverride={ind.cout_alimentaire_is_override}
                autoSub={`${ind.ratio_alimentaire_pct}% du forecast (${fmt(ind.ca_forecast_mois)})`}
                icon={Wallet}
                color="text-amber-600"
                onSave={updateCoutAlimentaireManuel}
                fmt={fmt}
                testid="kpi-alimentaire"
              />
              <KpiCard title="Achats matière première" value={fmt(ind.achats_mp_reel)} icon={DollarSign}
                sub={ind.ca_forecast_mois > 0 ? `Ratio réel : ${ind.ratio_mp_reel}% du forecast` : 'Pas de forecast ce mois'}
                color={ind.ratio_mp_reel > ind.ratio_alimentaire_pct ? 'text-red-600' : 'text-green-600'} testid="kpi-mp" />
              <KpiCard title="Ratio charges / CA" value={`${ind.ratio_charges_ca || 0}%`} icon={BarChart3}
                sub={ind.ca_objectif > 0 ? `CA objectif: ${fmt(ind.ca_objectif)}` : 'Définir CA objectif'} color="text-purple-600" testid="kpi-ratio" />
            </div>

            {/* Break-even detail */}
            <Card data-testid="breakeven-detail">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2"><Target className="w-5 h-5 text-blue-600" /> Seuil de rentabilité</CardTitle>
                <CardDescription>Chiffre d'affaires minimum pour couvrir toutes vos charges</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total charges mensuelles</p>
                    <p className="text-xl font-bold">{fmt(ind.total_mensuel)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Marge brute</p>
                    <p className="text-xl font-bold">{ind.marge_brute_pct}%</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Jours d'ouverture / mois</p>
                    <p className="text-xl font-bold">{ind.jours_ouverture} jours</p>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <p className="text-sm text-blue-700 font-medium">Break-even mensuel</p>
                    <p className="text-3xl font-bold text-blue-800 tabular-nums">{fmt(ind.break_even_mensuel)}</p>
                    <p className="text-xs text-blue-600 mt-1">= Total charges (fixes + variables + dettes) / Marge brute</p>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-sm text-emerald-700 font-medium">Break-even par jour d'ouverture</p>
                    <p className="text-3xl font-bold text-emerald-800 tabular-nums">{fmt(ind.break_even_jour)}</p>
                    <p className="text-xs text-emerald-600 mt-1">= Break-even mensuel / {ind.jours_ouverture} jours</p>
                  </div>
                </div>
                {ind.ca_objectif > 0 && (
                  <div className={`mt-4 p-3 rounded-lg border ${ind.ca_vs_breakeven >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2">
                      {ind.ca_vs_breakeven >= 0 ? <TrendingUp className="w-5 h-5 text-green-600" /> : <AlertTriangle className="w-5 h-5 text-red-600" />}
                      <span className={`font-semibold ${ind.ca_vs_breakeven >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {ind.ca_vs_breakeven >= 0
                          ? `Marge de sécurité : +${fmt(ind.ca_vs_breakeven)} au-dessus du break-even`
                          : `Alerte : ${fmt(Math.abs(ind.ca_vs_breakeven))} en dessous du break-even`}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Achats matière première & dépenses réelles du mois */}
            {ind.depenses_detail && ind.depenses_detail.length > 0 && (
              <Card data-testid="depenses-mois-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-orange-600" /> Dépenses réelles du mois
                      </CardTitle>
                      <CardDescription>Ventilation des dépenses importées (Linxo) pour ce mois</CardDescription>
                    </div>
                    <Badge variant="outline" className="text-base tabular-nums">{fmt(ind.total_depenses_reelles)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Catégorie</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead className="w-[120px]">Part</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ind.depenses_detail.map((d, i) => {
                        const pct = ind.total_depenses_reelles > 0 ? (d.montant / ind.total_depenses_reelles * 100) : 0;
                        const isMP = d.categorie === 'Achats Alimentaires';
                        return (
                          <TableRow key={i} className={isMP ? 'bg-amber-50/50 font-medium' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {isMP && <Badge variant="default" className="text-xs">MP</Badge>}
                                {d.categorie}
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{fmt(d.montant)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${isMP ? 'bg-amber-500' : 'bg-slate-400'}`}
                                    style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs tabular-nums text-muted-foreground w-10">{pct.toFixed(0)}%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Comparaison estimé vs réel matière première */}
            {(ind.variable_alimentaire > 0 || ind.achats_mp_reel > 0) && (
              <Card data-testid="comparaison-mp">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Matière première : estimé vs réel</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <p className="text-sm text-amber-700 font-medium">Estimé ({ind.ratio_alimentaire_pct}% forecast)</p>
                      <p className="text-2xl font-bold text-amber-800 tabular-nums">{fmt(ind.variable_alimentaire)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <p className="text-sm text-blue-700 font-medium">Achats réels (Linxo)</p>
                      <p className="text-2xl font-bold text-blue-800 tabular-nums">{fmt(ind.achats_mp_reel)}</p>
                      {ind.ca_forecast_mois > 0 && <p className="text-xs text-blue-600 mt-1">Ratio réel : {ind.ratio_mp_reel}%</p>}
                    </div>
                    <div className={`p-4 rounded-lg border ${ind.achats_mp_reel <= ind.variable_alimentaire ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <p className={`text-sm font-medium ${ind.achats_mp_reel <= ind.variable_alimentaire ? 'text-green-700' : 'text-red-700'}`}>Écart</p>
                      <p className={`text-2xl font-bold tabular-nums ${ind.achats_mp_reel <= ind.variable_alimentaire ? 'text-green-800' : 'text-red-800'}`}>
                        {ind.achats_mp_reel <= ind.variable_alimentaire ? '-' : '+'}{fmt(Math.abs(ind.achats_mp_reel - ind.variable_alimentaire))}
                      </p>
                      <p className={`text-xs mt-1 ${ind.achats_mp_reel <= ind.variable_alimentaire ? 'text-green-600' : 'text-red-600'}`}>
                        {ind.achats_mp_reel <= ind.variable_alimentaire ? 'Sous le budget' : 'Dépassement budget'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 6-month forecast */}
            <Card data-testid="prevision-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2"><Calendar className="w-5 h-5" /> Trésorerie prévisionnelle — 6 mois</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mois</TableHead>
                      <TableHead className="text-right">Entrées (CA)</TableHead>
                      <TableHead className="text-right">Sorties</TableHead>
                      <TableHead className="text-right">Solde</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ind.prevision_6_mois?.map((p, i) => (
                      <TableRow key={i} className={p.alerte ? 'bg-red-50/50' : ''} data-testid={`prevision-row-${i}`}>
                        <TableCell className="font-medium">{p.mois}</TableCell>
                        <TableCell className="text-right tabular-nums text-green-600">{fmt(p.entrees)}</TableCell>
                        <TableCell className="text-right tabular-nums text-red-600">-{fmt(p.sorties)}</TableCell>
                        <TableCell className={`text-right tabular-nums font-bold ${p.solde < 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(p.solde)}</TableCell>
                        <TableCell>{p.alerte && <AlertTriangle className="w-4 h-4 text-red-500" />}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* =============== CHARGES TAB =============== */}
          <TabsContent value="charges" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Charges mensuelles</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setCatForm({ name: '', type: 'fixe' }); setEditItem(null); setShowCatDialog(true); }}>
                  <Settings className="w-4 h-4 mr-1" /> Catégories
                </Button>
                <Button size="sm" onClick={() => openChargeDialog()} data-testid="add-charge-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nouvelle charge
                </Button>
              </div>
            </div>

            {/* Charges fixes */}
            <ChargesTable title="Charges fixes" charges={charges.filter(c => c.type === 'fixe')}
              onEdit={openChargeDialog} onDelete={deleteCharge} onInlineUpdate={updateChargeField} fmt={fmt} />

            {/* Charges variables */}
            <ChargesTable title="Charges variables" charges={charges.filter(c => c.type !== 'fixe')}
              onEdit={openChargeDialog} onDelete={deleteCharge} onInlineUpdate={updateChargeField} fmt={fmt} />
          </TabsContent>

          {/* =============== DETTES TAB =============== */}
          <TabsContent value="dettes" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Dettes & Échéancier</h2>
              <Button size="sm" onClick={() => openDetteDialog()} data-testid="add-dette-btn">
                <Plus className="w-4 h-4 mr-1" /> Nouvelle dette
              </Button>
            </div>

            {/* Emprunts */}
            <DettesSection title="Emprunts bancaires" dettes={dettes.filter(d => d.type === 'emprunt')}
              onEdit={openDetteDialog} onDelete={deleteDette} fmt={fmt} />

            {/* Fournisseurs */}
            <DettesSection title="Dettes fournisseurs" dettes={dettes.filter(d => d.type === 'fournisseur')}
              onEdit={openDetteDialog} onDelete={deleteDette} fmt={fmt} />
          </TabsContent>

          {/* =============== CONFIG TAB =============== */}
          <TabsContent value="config" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Paramètres du pilotage</CardTitle>
                <CardDescription>Ces valeurs servent au calcul du break-even et des indicateurs</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Marge brute moyenne (%)</Label>
                      <Button type="button" size="sm" variant="outline" onClick={openMargeCalculator} data-testid="btn-calculer-marge" className="h-7 text-xs">
                        <Calculator className="w-3.5 h-3.5 mr-1" /> Calculer ma marge
                      </Button>
                    </div>
                    <Input type="number" min={0} max={100} step={0.1} value={config?.marge_brute_pct || ''}
                      onChange={e => setConfig(p => ({ ...p, marge_brute_pct: parseFloat(e.target.value) || 0 }))}
                      data-testid="config-marge" />
                    <p className="text-xs text-muted-foreground">Ex: 65% signifie que pour 100EUR de CA, 65EUR couvrent les charges</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Jours d'ouverture par mois</Label>
                    <Input type="number" min={1} max={31} value={config?.jours_ouverture_mois || ''}
                      onChange={e => setConfig(p => ({ ...p, jours_ouverture_mois: parseInt(e.target.value) || 0 }))}
                      data-testid="config-jours" />
                  </div>
                  <div className="space-y-2">
                    <Label>Solde de trésorerie actuel (EUR)</Label>
                    <Input type="number" step={0.01} value={config?.solde_tresorerie || ''}
                      onChange={e => setConfig(p => ({ ...p, solde_tresorerie: parseFloat(e.target.value) || 0 }))}
                      data-testid="config-solde" />
                  </div>
                  <div className="space-y-2">
                    <Label>CA mensuel objectif (EUR)</Label>
                    <Input type="number" step={0.01} value={config?.ca_mensuel_objectif || ''}
                      onChange={e => setConfig(p => ({ ...p, ca_mensuel_objectif: parseFloat(e.target.value) || 0 }))}
                      data-testid="config-ca" />
                  </div>
                  <div className="space-y-2">
                    <Label>Ratio alimentaire (%)</Label>
                    <Input type="number" min={0} max={100} step={0.1} value={config?.ratio_alimentaire_pct ?? 30}
                      onChange={e => setConfig(p => ({ ...p, ratio_alimentaire_pct: parseFloat(e.target.value) || 0 }))}
                      data-testid="config-ratio-alim" />
                    <p className="text-xs text-muted-foreground">Pourcentage du CA prévisionnel appliqué comme charge variable alimentaire</p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveConfig} disabled={saving} data-testid="save-config-btn">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sauvegarder
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Categories management */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Catégories de charges</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => { setCatForm({ name: '', type: 'fixe' }); setEditItem(null); setShowCatDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map(cat => (
                      <TableRow key={cat.id}>
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        <TableCell><Badge variant={cat.type === 'fixe' ? 'default' : 'secondary'}>{cat.type}</Badge></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => { setCatForm({ name: cat.name, type: cat.type }); setEditItem(cat); setShowCatDialog(true); }}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteCat(cat.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ===== DIALOGS ===== */}
        {/* Charge Dialog */}
        <Dialog open={showChargeDialog} onOpenChange={setShowChargeDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Nouvelle'} charge</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Catégorie</Label>
                <Select value={chargeForm.categorie} onValueChange={v => setChargeForm(p => ({ ...p, categorie: v }))}>
                  <SelectTrigger><SelectValue placeholder="Catégorie" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Input value={chargeForm.description} onChange={e => setChargeForm(p => ({ ...p, description: e.target.value }))} placeholder="Ex: Loyer local commercial" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Montant (EUR)</Label>
                  <Input type="number" step={0.01} value={chargeForm.montant} onChange={e => setChargeForm(p => ({ ...p, montant: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Fréquence</Label>
                  <Select value={chargeForm.frequence} onValueChange={v => setChargeForm(p => ({ ...p, frequence: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensuel">Mensuel</SelectItem>
                      <SelectItem value="trimestriel">Trimestriel</SelectItem>
                      <SelectItem value="annuel">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={chargeForm.type} onValueChange={v => setChargeForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixe">Fixe</SelectItem>
                    <SelectItem value="variable">Variable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChargeDialog(false)}>Annuler</Button>
              <Button onClick={saveCharge} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dette Dialog */}
        <Dialog open={showDetteDialog} onOpenChange={setShowDetteDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Nouvelle'} dette</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={detteForm.type} onValueChange={v => setDetteForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emprunt">Emprunt bancaire</SelectItem>
                    <SelectItem value="fournisseur">Fournisseur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Libellé</Label>
                <Input value={detteForm.libelle} onChange={e => setDetteForm(p => ({ ...p, libelle: e.target.value }))} placeholder="Ex: Emprunt BNP travaux" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Montant initial (EUR)</Label>
                  <Input type="number" step={0.01} value={detteForm.montant_initial} onChange={e => setDetteForm(p => ({ ...p, montant_initial: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Montant restant (EUR)</Label>
                  <Input type="number" step={0.01} value={detteForm.montant_restant} onChange={e => setDetteForm(p => ({ ...p, montant_restant: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Taux (%)</Label>
                  <Input type="number" step={0.01} value={detteForm.taux} onChange={e => setDetteForm(p => ({ ...p, taux: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Mensualité (EUR)</Label>
                  <Input type="number" step={0.01} value={detteForm.mensualite} onChange={e => setDetteForm(p => ({ ...p, mensualite: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Durée (mois)</Label>
                  <Input type="number" value={detteForm.duree_mois} onChange={e => setDetteForm(p => ({ ...p, duree_mois: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Date début</Label>
                  <Input type="date" value={detteForm.date_debut} onChange={e => setDetteForm(p => ({ ...p, date_debut: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Date échéance</Label>
                  <Input type="date" value={detteForm.date_echeance} onChange={e => setDetteForm(p => ({ ...p, date_echeance: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input value={detteForm.notes} onChange={e => setDetteForm(p => ({ ...p, notes: e.target.value }))} placeholder="Commentaire..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetteDialog(false)}>Annuler</Button>
              <Button onClick={saveDette} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Dialog */}
        <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Nouvelle'} catégorie</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Nom</Label>
                <Input value={catForm.name} onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Marketing" />
              </div>
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={catForm.type} onValueChange={v => setCatForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixe">Fixe</SelectItem>
                    <SelectItem value="variable">Variable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCatDialog(false)}>Annuler</Button>
              <Button onClick={saveCat}>Sauvegarder</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Calcul Marge Brute */}
        <Dialog open={showMargeCalc} onOpenChange={setShowMargeCalc}>
          <DialogContent className="max-w-2xl" data-testid="dialog-calcul-marge">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" /> Calcul de ta marge brute réelle
              </DialogTitle>
              <CardDescription>Basé sur tes vrais plats et tes achats réels (Linxo)</CardDescription>
            </DialogHeader>

            {margeCalcLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : margeCalcData ? (
              <div className="space-y-4">
                {/* Méthode 1 */}
                <Card className={margeCalcData.source === 'rentabilite' ? 'border-blue-400 bg-blue-50/30' : ''}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">Méthode 1</Badge>
                          <span className="font-semibold text-sm">Via tes plats Rentabilité</span>
                          {margeCalcData.source === 'rentabilite' && <Badge className="text-xs">Suggéré</Badge>}
                        </div>
                        <p className="text-3xl font-bold tabular-nums mt-2 text-blue-600" data-testid="marge-rentabilite">
                          {margeCalcData.marge_rentabilite != null ? `${margeCalcData.marge_rentabilite}%` : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {margeCalcData.nb_plats_analyses} plats analysés
                          {margeCalcData.total_ventes_plats > 0 && ` • pondération par ${margeCalcData.total_ventes_plats} ventes`}
                          <br />
                          CA potentiel : {fmt(margeCalcData.ca_potentiel_plats)} • Coût matières : {fmt(margeCalcData.cout_total_plats)}
                        </p>
                      </div>
                      {margeCalcData.marge_rentabilite != null && (
                        <Button size="sm" variant="outline" onClick={() => applyMarge(margeCalcData.marge_rentabilite)} data-testid="apply-marge-rentabilite">
                          Appliquer
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Méthode 2 */}
                <Card className={margeCalcData.source === 'linxo' ? 'border-green-400 bg-green-50/30' : ''}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">Méthode 2</Badge>
                          <span className="font-semibold text-sm">Via tes données réelles (Linxo, 3 derniers mois)</span>
                          {margeCalcData.source === 'linxo' && <Badge className="text-xs">Suggéré ✓</Badge>}
                        </div>
                        <p className="text-3xl font-bold tabular-nums mt-2 text-green-600" data-testid="marge-linxo">
                          {margeCalcData.marge_linxo != null ? `${margeCalcData.marge_linxo}%` : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          CA 3 mois : {fmt(margeCalcData.ca_reel_3mois)} • Achats alimentaires : {fmt(margeCalcData.achats_alim_3mois)}
                        </p>
                        {margeCalcData.mois_details.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {margeCalcData.mois_details.map((m) => (
                              <div key={`${m.annee}-${m.mois}`} className="text-xs px-2 py-1 rounded bg-white border">
                                {String(m.mois).padStart(2, '0')}/{m.annee} : <span className="font-semibold">{m.marge_pct != null ? `${m.marge_pct}%` : '—'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {margeCalcData.marge_linxo != null && (
                        <Button size="sm" variant="outline" onClick={() => applyMarge(margeCalcData.marge_linxo)} data-testid="apply-marge-linxo">
                          Appliquer
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Suggestion finale */}
                {margeCalcData.suggestion != null && (
                  <Card className="border-amber-400 bg-amber-50">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-600" />
                            <span className="font-semibold text-sm text-amber-800">Recommandation finale</span>
                          </div>
                          <p className="text-4xl font-bold tabular-nums mt-2 text-amber-700" data-testid="marge-suggestion">
                            {margeCalcData.suggestion}%
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Source : {margeCalcData.source === 'linxo' ? 'données bancaires réelles (Linxo)' : 'plats Rentabilité'}
                            {margeCalcData.alerte && <span className="block mt-1 text-red-700">⚠️ {margeCalcData.alerte}</span>}
                          </p>
                        </div>
                        <Button onClick={() => applyMarge(margeCalcData.suggestion)} className="bg-amber-600 hover:bg-amber-700" data-testid="apply-marge-suggestion">
                          <Sparkles className="w-4 h-4 mr-1" /> Appliquer {margeCalcData.suggestion}%
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {margeCalcData.suggestion == null && (
                  <div className="p-4 rounded-md bg-muted/50 text-sm text-muted-foreground text-center">
                    Pas assez de données pour calculer. Remplis tes plats Rentabilité ou importe des relevés Linxo.
                  </div>
                )}
              </div>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMargeCalc(false)}>Fermer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}

// ===== Sub-components =====

function KpiCard({ title, value, icon: Icon, sub, color, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{title}</p>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-muted/50`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCardEditable({ title, value, manualValue, isOverride, autoSub, icon: Icon, color, onSave, fmt, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(manualValue || ''));

  useEffect(() => { setDraft(String(manualValue || '')); }, [manualValue]);

  const commit = () => {
    setEditing(false);
    const num = parseFloat(String(draft).replace(',', '.')) || 0;
    if (num !== Number(manualValue || 0)) onSave(num);
  };

  const reset = () => {
    onSave(0);
    setDraft('');
  };

  return (
    <Card data-testid={testid}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground font-medium">{title}</p>
              {isOverride && <Badge variant="default" className="text-[9px] h-4 px-1">Override</Badge>}
            </div>
            {editing ? (
              <Input
                type="number"
                step="0.01"
                autoFocus
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={e => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') { setDraft(String(manualValue || '')); setEditing(false); }
                }}
                className="h-9 mt-1 font-bold tabular-nums"
                placeholder="Laisser vide pour auto"
                data-testid={`${testid}-input`}
              />
            ) : (
              <button
                onClick={() => setEditing(true)}
                className={`text-2xl font-bold tabular-nums mt-1 text-left hover:underline ${color}`}
                data-testid={`${testid}-value`}
              >
                {fmt(value)}
              </button>
            )}
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-muted-foreground">
                {isOverride ? 'Valeur saisie manuellement' : autoSub}
              </p>
              {isOverride && (
                <button
                  onClick={reset}
                  className="text-[10px] text-blue-600 hover:underline"
                  data-testid={`${testid}-reset`}
                >
                  revenir à l'auto
                </button>
              )}
            </div>
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-muted/50 shrink-0`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChargesTable({ title, charges, onEdit, onDelete, onInlineUpdate, fmt }) {
  const total = charges.reduce((s, c) => {
    let m = c.montant || 0;
    if (c.frequence === 'trimestriel') m /= 3;
    if (c.frequence === 'annuel') m /= 12;
    return s + m;
  }, 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <Badge variant="outline">{fmt(total)} / mois</Badge>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Catégorie</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead>Fréquence</TableHead>
              <TableHead className="text-right">/ mois</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {charges.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Aucune charge</TableCell></TableRow>
            )}
            {charges.map(c => {
              let mensuel = c.montant || 0;
              if (c.frequence === 'trimestriel') mensuel /= 3;
              if (c.frequence === 'annuel') mensuel /= 12;
              return (
                <TableRow key={c.id}>
                  <TableCell><Badge variant="secondary" className="text-xs">{c.categorie}</Badge></TableCell>
                  <TableCell className="text-sm">{c.description}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    <InlineMontantInput
                      value={c.montant || 0}
                      onSave={(v) => onInlineUpdate(c.id, 'montant', v)}
                      testId={`charge-montant-${c.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <InlineFrequenceSelect
                      value={c.frequence || 'mensuel'}
                      onSave={(v) => onInlineUpdate(c.id, 'frequence', v)}
                      testId={`charge-freq-${c.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{fmt(mensuel)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(c)}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(c.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function InlineMontantInput({ value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(value ?? 0));

  useEffect(() => { setVal(String(value ?? 0)); }, [value]);

  const commit = () => {
    const num = parseFloat(String(val).replace(',', '.')) || 0;
    setEditing(false);
    if (num !== Number(value)) onSave(num);
  };

  if (editing) {
    return (
      <Input
        type="number"
        step="0.01"
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setVal(String(value ?? 0)); setEditing(false); }
        }}
        className="h-8 w-28 text-right tabular-nums ml-auto"
        data-testid={testId}
      />
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="px-2 py-1 rounded hover:bg-accent transition tabular-nums w-full text-right"
      data-testid={`${testId}-display`}
    >
      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0)}
    </button>
  );
}

function InlineFrequenceSelect({ value, onSave, testId }) {
  return (
    <Select value={value} onValueChange={v => { if (v !== value) onSave(v); }}>
      <SelectTrigger className="h-8 w-32" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="mensuel">Mensuel</SelectItem>
        <SelectItem value="trimestriel">Trimestriel</SelectItem>
        <SelectItem value="annuel">Annuel</SelectItem>
      </SelectContent>
    </Select>
  );
}

function DettesSection({ title, dettes, onEdit, onDelete, fmt }) {
  const total = dettes.reduce((s, d) => s + (d.montant_restant || 0), 0);
  const totalMens = dettes.reduce((s, d) => s + (d.mensualite || 0), 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <div className="flex gap-2">
            <Badge variant="outline">{fmt(totalMens)} / mois</Badge>
            <Badge variant="destructive">{fmt(total)} restant</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Libellé</TableHead>
              <TableHead className="text-right">Initial</TableHead>
              <TableHead className="text-right">Restant</TableHead>
              <TableHead className="text-right">Mensualité</TableHead>
              <TableHead>Taux</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dettes.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Aucune dette</TableCell></TableRow>
            )}
            {dettes.map(d => {
              const pct = d.montant_initial > 0 ? ((d.montant_initial - d.montant_restant) / d.montant_initial * 100) : 0;
              return (
                <TableRow key={d.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{d.libelle}</p>
                      {d.notes && <p className="text-xs text-muted-foreground">{d.notes}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(d.montant_initial)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-red-600">{fmt(d.montant_restant)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(d.mensualite)}</TableCell>
                  <TableCell className="text-sm">{d.taux}%</TableCell>
                  <TableCell className="text-sm">{d.date_echeance || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(d)}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(d.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
